
import { useState } from 'react';
import MasterNavigation from '../../components/feature/MasterNavigation';

interface SecurityReport {
  id: string;
  title: string;
  type: 'Penetration Test' | 'Vulnerability Assessment' | 'Code Review' | 'Compliance Audit';
  status: 'In Progress' | 'Under Review' | 'Completed' | 'Action Required';
  severity: 'Critical' | 'High' | 'Medium' | 'Low';
  date: string;
  findings: number;
  resolved: number;
}

interface UpcomingMeeting {
  id: string;
  title: string;
  date: string;
  time: string;
  type: 'Review Meeting' | 'Follow-up' | 'Consultation' | 'Training';
  attendees: number;
}

export default function ClientPortalPage() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginStatus, setLoginStatus] = useState<'idle' | 'loading' | 'error'>('idle');

  // Mock client data
  const securityReports: SecurityReport[] = [
    {
      id: '1',
      title: 'Web Application Security Assessment',
      type: 'Penetration Test',
      status: 'Action Required',
      severity: 'Critical',
      date: '2024-01-15',
      findings: 8,
      resolved: 3
    },
    {
      id: '2',
      title: 'Network Infrastructure Review',
      type: 'Vulnerability Assessment',
      status: 'Under Review',
      severity: 'High',
      date: '2024-01-10',
      findings: 12,
      resolved: 9
    },
    {
      id: '3',
      title: 'SOC 2 Compliance Audit',
      type: 'Compliance Audit',
      status: 'Completed',
      severity: 'Medium',
      date: '2024-01-05',
      findings: 5,
      resolved: 5
    }
  ];

  const upcomingMeetings: UpcomingMeeting[] = [
    {
      id: '1',
      title: 'Quarterly Security Review',
      date: '2024-01-25',
      time: '2:00 PM EST',
      type: 'Review Meeting',
      attendees: 4
    },
    {
      id: '2',
      title: 'Remediation Follow-up',
      date: '2024-01-28',
      time: '10:00 AM EST',
      type: 'Follow-up',
      attendees: 2
    }
  ];

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginStatus('loading');

    // Simulate login process
    setTimeout(() => {
      if (loginEmail && loginPassword) {
        setIsLoggedIn(true);
        setLoginStatus('idle');
      } else {
        setLoginStatus('error');
        setTimeout(() => setLoginStatus('idle'), 3000);
      }
    }, 1500);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Critical': return 'text-red-400 bg-red-900/20 border-red-700';
      case 'High': return 'text-orange-400 bg-orange-900/20 border-orange-700';
      case 'Medium': return 'text-yellow-400 bg-yellow-900/20 border-yellow-700';
      case 'Low': return 'text-green-400 bg-green-900/20 border-green-700';
      case 'Action Required': return 'text-red-400 bg-red-900/20 border-red-700';
      case 'Under Review': return 'text-yellow-400 bg-yellow-900/20 border-yellow-700';
      case 'Completed': return 'text-green-400 bg-green-900/20 border-green-700';
      case 'In Progress': return 'text-blue-400 bg-blue-900/20 border-blue-700';
      default: return 'text-gray-400 bg-gray-900/20 border-gray-700';
    }
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-black">
        {/* Header */}
        <header className="bg-black border-b border-gray-800">
          <div className="container mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img src="https://static.readdy.ai/image/fc9008f476a46e5d11a11722891fc192/828492aec85b774ecdf3a4106883a9bc.png" alt="TIDHQ.NETWORK" className="h-10 w-10 object-contain" />
                <div>
                  <h1 className="text-white font-light text-xl">TIDHQ.NETWORK</h1>
                  <p className="text-gray-400 text-xs">Client Portal</p>
                </div>
              </div>
              <MasterNavigation currentPath="/client-portal" />
            </div>
          </div>
        </header>

        <main className="container mx-auto px-6 py-12">
          <div className="max-w-md mx-auto">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-light text-white mb-4">Client Portal Login</h1>
              <p className="text-gray-400">Access your security reports and schedule follow-ups</p>
            </div>

            <div className="bg-gray-900 border border-gray-700 rounded-xl p-8">
              <form onSubmit={handleLogin} className="space-y-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-[#1A3A2E] focus:ring-1 focus:ring-[#1A3A2E]"
                    placeholder="Enter your email"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                    Password
                  </label>
                  <input
                    type="password"
                    id="password"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-[#1A3A2E] focus:ring-1 focus:ring-[#1A3A2E]"
                    placeholder="Enter your password"
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={loginStatus === 'loading'}
                  className="w-full bg-[#1A3A2E] hover:bg-[#2A4A3E] text-white py-3 px-4 rounded-lg transition-all hover:transform hover:scale-105 flex items-center justify-center gap-2 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loginStatus === 'loading' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      <span>Signing In...</span>
                    </>
                  ) : (
                    <>
                      <i className="ri-login-box-line"></i>
                      <span>Sign In</span>
                    </>
                  )}
                </button>

                {loginStatus === 'error' && (
                  <div className="p-3 bg-red-900 border border-red-700 rounded-lg text-red-100 text-sm flex items-center gap-2">
                    <i className="ri-error-warning-line"></i>
                    <span>Invalid credentials. Please try again.</span>
                  </div>
                )}
              </form>

              <div className="mt-6 pt-6 border-t border-gray-700 text-center">
                <p className="text-gray-400 text-sm">
                  Don't have an account?{' '}
                  <a href="/client-registration" className="text-[#1A3A2E] hover:text-[#2A4A3E] transition-colors">
                    Register here
                  </a>
                </p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="bg-black border-b border-gray-800">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="https://static.readdy.ai/image/fc9008f476a46e5d11a11722891fc192/828492aec85b774ecdf3a4106883a9bc.png" alt="TIDHQ.NETWORK" className="h-10 w-10 object-contain" />
              <div>
                <h1 className="text-white font-light text-xl">TIDHQ.NETWORK</h1>
                <p className="text-gray-400 text-xs">Client Dashboard</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-gray-400 text-sm">Welcome back, Alex Johnson</span>
              <button
                onClick={() => setIsLoggedIn(false)}
                className="text-gray-400 hover:text-white transition-colors"
                title="Sign Out"
              >
                <i className="ri-logout-box-line text-lg"></i>
              </button>
              <MasterNavigation currentPath="/client-portal" />
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8">
        {/* Dashboard Stats */}
        <div className="grid md:grid-cols-4 gap-6 mb-12">
          <div className="bg-gray-900 border border-gray-700 rounded-xl p-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-gray-300 text-sm font-medium">Active Reports</h3>
              <i className="ri-file-list-3-line text-[#1A3A2E] text-xl"></i>
            </div>
            <div className="text-2xl font-light text-white mb-1">3</div>
            <div className="text-xs text-gray-400">2 require action</div>
          </div>

          <div className="bg-gray-900 border border-gray-700 rounded-xl p-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-gray-300 text-sm font-medium">Critical Findings</h3>
              <i className="ri-alarm-warning-line text-red-400 text-xl"></i>
            </div>
            <div className="text-2xl font-light text-white mb-1">8</div>
            <div className="text-xs text-gray-400">3 resolved</div>
          </div>

          <div className="bg-gray-900 border border-gray-700 rounded-xl p-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-gray-300 text-sm font-medium">Security Score</h3>
              <i className="ri-shield-check-line text-[#1A3A2E] text-xl"></i>
            </div>
            <div className="text-2xl font-light text-white mb-1">B+</div>
            <div className="text-xs text-gray-400">Improving</div>
          </div>

          <div className="bg-gray-900 border border-gray-700 rounded-xl p-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-gray-300 text-sm font-medium">Next Meeting</h3>
              <i className="ri-calendar-line text-[#1A3A2E] text-xl"></i>
            </div>
            <div className="text-2xl font-light text-white mb-1">Jan 25</div>
            <div className="text-xs text-gray-400">2:00 PM EST</div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Security Reports */}
          <div className="lg:col-span-2">
            <div className="bg-gray-900 border border-gray-700 rounded-xl">
              <div className="p-6 border-b border-gray-700">
                <h2 className="text-xl font-medium text-white">Security Reports</h2>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {securityReports.map((report) => (
                    <div key={report.id} className="bg-gray-800 border border-gray-600 rounded-lg p-4 hover:border-[#1A3A2E] transition-colors cursor-pointer">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="text-white font-medium mb-1">{report.title}</h3>
                          <div className="flex items-center gap-3 text-sm text-gray-400">
                            <span>{report.type}</span>
                            <span>•</span>
                            <span>{new Date(report.date).toLocaleDateString()}</span>
                          </div>
                        </div>
                        <div className="flex flex-col gap-2">
                          <span className={`px-2 py-1 rounded text-xs border ${getStatusColor(report.status)}`}>
                            {report.status}
                          </span>
                          <span className={`px-2 py-1 rounded text-xs border ${getStatusColor(report.severity)}`}>
                            {report.severity}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <div className="text-gray-400">
                          <span className="text-white">{report.resolved}</span>/{report.findings} findings resolved
                        </div>
                        <div className="flex-1 bg-gray-700 rounded-full h-2">
                          <div
                            className="bg-[#1A3A2E] h-2 rounded-full transition-all"
                            style={{ width: `${(report.resolved / report.findings) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="bg-gray-900 border border-gray-700 rounded-xl p-6">
              <h3 className="text-lg font-medium text-white mb-4">Quick Actions</h3>
              <div className="space-y-3">
                <button className="w-full bg-[#1A3A2E] hover:bg-[#2A4A3E] text-white py-3 px-4 rounded-lg transition-all hover:transform hover:scale-105 flex items-center gap-3 whitespace-nowrap">
                  <i className="ri-calendar-line"></i>
                  <span>Schedule Follow-up</span>
                </button>
                <button className="w-full border border-gray-600 hover:border-gray-400 text-gray-300 hover:text-white py-3 px-4 rounded-lg transition-all flex items-center gap-3 whitespace-nowrap">
                  <i className="ri-download-line"></i>
                  <span>Download Reports</span>
                </button>
                <button
                  onClick={() => {
                    const widget = document.querySelector('#vapi-widget-floating-button') as HTMLElement;
                    if (widget) widget.click();
                  }}
                  className="w-full border border-gray-600 hover:border-gray-400 text-gray-300 hover:text-white py-3 px-4 rounded-lg transition-all flex items-center gap-3 whitespace-nowrap"
                >
                  <i className="ri-customer-service-2-line"></i>
                  <span>Contact Team</span>
                </button>
              </div>
            </div>

            {/* Upcoming Meetings */}
            <div className="bg-gray-900 border border-gray-700 rounded-xl p-6">
              <h3 className="text-lg font-medium text-white mb-4">Upcoming Meetings</h3>
              <div className="space-y-4">
                {upcomingMeetings.map((meeting) => (
                  <div key={meeting.id} className="border border-gray-700 rounded-lg p-4">
                    <h4 className="text-white font-medium mb-2">{meeting.title}</h4>
                    <div className="space-y-1 text-sm text-gray-400">
                      <div className="flex items-center gap-2">
                        <i className="ri-calendar-line text-[#1A3A2E]"></i>
                        <span>{new Date(meeting.date).toLocaleDateString()}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <i className="ri-time-line text-[#1A3A2E]"></i>
                        <span>{meeting.time}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <i className="ri-group-line text-[#1A3A2E]"></i>
                        <span>{meeting.attendees} attendees</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 border-t border-gray-800 py-12 mt-12">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="flex items-center gap-3 mb-6 md:mb-0">
              <img src="https://static.readdy.ai/image/fc9008f476a46e5d11a11722891fc192/828492aec85b774ecdf3a4106883a9bc.png" alt="TIDHQ.NETWORK" className="h-8 w-8 object-contain" />
              <span className="text-gray-400">TIDHQ.NETWORK</span>
            </div>
            <div className="text-center md:text-right">
              <p className="text-gray-400 text-sm">© 2026 TIDHQ.NETWORK. All rights reserved.</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
